import * as React from 'react';
import { DataGridPro, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import {
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  ExportCsv,
  QuickFilter,
  QuickFilterTrigger,
  QuickFilterControl,
  GridFilterModel,
  GridSortModel,
  GridColumnsManagement,
  useGridApiContext,
  useGridRootProps,
  gridColumnFieldsSelector,
  gridColumnVisibilityModelSelector,
  useGridSelector,
} from '@mui/x-data-grid';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import Box from '@mui/material/Box/Box';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import SearchSvgUrl from 'Assets/Icons/search.svg';
import CloudySvgUrl from 'Assets/Icons/cloudy.svg';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'Context';
import { buildEndpoint, useApiWorker } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import './DataGrid.scss';

export interface LegacyParams {
  readEndpoint: string;
  searchParams?: Record<string, any>;
  apiVersion?: string;
}

// Shared context — filter components read values and push changes here instead of
// going through MUI's filterModel / upsertFilterItem, which has timing issues with
// controlled dropdowns whose option data loads asynchronously.
export interface FilterContextValue {
  values: Record<string, any>;
  onChange: (field: string, value: any) => void;
  /** Fields that are grayed-out / non-interactive due to the filter performance rule. */
  disabledFields?: Set<string>;
}
export const FilterContext = React.createContext<FilterContextValue>({
  values: {},
  onChange: () => {},
});

const SkeletonLoadingOverlay: React.FC = () => (
  <div className="datagrid-skeleton-overlay">
    {Array.from({ length: 10 }).map((_, rowIndex) => (
      <div key={rowIndex} className="datagrid-skeleton-row">
        {Array.from({ length: 5 }).map((_, colIndex) => (
          <div
            key={colIndex}
            className="skeleton skeleton-text"
            style={{ width: `${[60, 45, 70, 50, 55][colIndex % 5]}%` }}
          />
        ))}
      </div>
    ))}
  </div>
);

const NoRowsOverlay: React.FC = () => (
  <div
    style={{
      background: '#F6F7F9',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}
  >
    <img
      src={CloudySvgUrl}
      alt="No records"
      style={{ width: 100, marginBottom: 10 }}
    />
    <span
      style={{
        fontFamily: 'NotoSans',
        fontWeight: 500,
        fontSize: 24,
        lineHeight: '28.8px',
        textAlign: 'center',
        color: '#546778',
      }}
    >
      No records found
    </span>
    <span
      style={{
        fontFamily: 'NotoSans',
        fontWeight: 500,
        fontSize: 16,
        lineHeight: '20px',
        textAlign: 'center',
        color: '#546778',
      }}
    >
      Try adjusting your filters or adding new items to see results here.
    </span>
  </div>
);

const SearchIcon = () => (
  <img src={SearchSvgUrl} alt="" aria-hidden="true" width={24} height={24} />
);

export interface ToolbarPermissions {
  showSearch?: boolean;
  showFilter?: boolean;
  showExport?: boolean;
  showColumns?: boolean;
}

interface CustomToolbarProps {
  title?: string;
  onSearchCollapse?: () => void;
  headerActions?: React.ReactNode;
  onSearchChange?: (value: string) => void;
  showFilters?: boolean;
  onToggleFilters?: () => void;
  onExport?: () => void;
  toolbarPermissions?: ToolbarPermissions;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({
  title,
  headerActions,
  showFilters,
  onToggleFilters,
  onExport,
  toolbarPermissions,
  onSearchCollapse,
}) => {
  const perms = toolbarPermissions ?? {};
  const showColumns = perms.showColumns !== false;
  const showFilter = perms.showFilter !== false;
  const showExport = perms.showExport !== false;
  const showSearch = perms.showSearch !== false;
  return (
    <Toolbar>
      {title && <span className="datagrid-card-title">{title}</span>}
      <Box sx={{ flexGrow: 1 }} />
      {headerActions && (
        <div className="datagrid-toolbar-group datagrid-toolbar-group--action">
          {headerActions}
          <span className="datagrid-header-divider" />
        </div>
      )}
      <div className="datagrid-toolbar-group">
        {showColumns && (
          <ColumnsPanelTrigger
            render={
              <ToolbarButton>
                <ViewWeekOutlinedIcon />
              </ToolbarButton>
            }
          />
        )}
        {showFilter && (
          <ToolbarButton
            onClick={onToggleFilters}
            className={showFilters ? 'Mui-selected' : ''}
          >
            <FilterListRoundedIcon />
          </ToolbarButton>
        )}
        {(showColumns || showFilter) && (showExport || showSearch) && (
          <span className="datagrid-header-divider" />
        )}
        {showExport &&
          (onExport ? (
            <ToolbarButton onClick={onExport}>
              <FileDownloadRoundedIcon />
            </ToolbarButton>
          ) : (
            <ExportCsv
              render={
                <ToolbarButton>
                  <FileDownloadRoundedIcon />
                </ToolbarButton>
              }
            />
          ))}
        {showExport && showSearch && <span className="datagrid-header-divider" />}
        {showSearch && (
          <QuickFilter
            debounceMs={300}
            // Closing the search bar must also drop the search, otherwise the grid
            // stays filtered with no visible indication (STRAD2-2669 / TC-010).
            onExpandedChange={(expanded) => {
              if (!expanded) onSearchCollapse?.();
            }}
          >
            <QuickFilterTrigger
              render={
                <ToolbarButton>
                  <SearchIcon />
                </ToolbarButton>
              }
            />
            <QuickFilterControl
              className={(state) =>
                `datagrid-search-control${state.expanded ? ' datagrid-search-control--expanded' : ''}`
              }
            />
          </QuickFilter>
        )}
      </div>
    </Toolbar>
  );
};

const GridLoadingSkeleton: React.FC<{ title?: string }> = ({ title }) => (
  <div className="datagrid-skeleton-standalone">
    <div className="datagrid-skeleton-toolbar">
      {title && <span className="datagrid-card-title">{title}</span>}
      <div style={{ flex: 1 }} />
      <div className="datagrid-skeleton-toolbar-icons">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton skeleton-icon" />
        ))}
      </div>
    </div>
    <div className="datagrid-skeleton-header">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="skeleton skeleton-col-header" />
      ))}
    </div>
    {Array.from({ length: 10 }).map((_, rowIndex) => (
      <div key={rowIndex} className="datagrid-skeleton-row">
        {Array.from({ length: 5 }).map((_, colIndex) => (
          <div
            key={colIndex}
            className="skeleton skeleton-text"
            style={{ width: `${[60, 45, 70, 50, 55][colIndex % 5]}%` }}
          />
        ))}
      </div>
    ))}
  </div>
);

// Defined outside DataGrid so MUI doesn't remount the panel on every parent render.
const CustomColumnsManagement: React.FC<any> = ({
  onResetColumns,
  defaultVisibility,
  defaultFields,
  ...rest
}) => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const columnVisibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector
  );
  const currentFields = useGridSelector(apiRef, gridColumnFieldsSelector);

  const isResetDisabled = React.useMemo(() => {
    const dv: Record<string, boolean> = defaultVisibility ?? {};
    const df: string[] = defaultFields ?? [];
    const hiddenNow = Object.keys(columnVisibilityModel)
      .filter((k) => columnVisibilityModel[k] === false)
      .sort((a, b) => a.localeCompare(b));
    const hiddenDef = Object.keys(dv)
      .filter((k) => dv[k] === false)
      .sort((a, b) => a.localeCompare(b));
    const visMatch =
      hiddenNow.length === hiddenDef.length &&
      hiddenNow.every((k, i) => k === hiddenDef[i]);
    const orderMatch =
      currentFields.filter((f: string) => df.includes(f)).join(',') ===
      df.join(',');
    return visMatch && orderMatch;
  }, [columnVisibilityModel, currentFields, defaultVisibility, defaultFields]);

  const BaseButton = rootProps.slots.baseButton as React.ComponentType<any>;
  return (
    <>
      <GridColumnsManagement {...rest} disableResetButton />
      <div className="datagrid-columns-reset-footer">
        <BaseButton
          size="small"
          disabled={isResetDisabled}
          onClick={onResetColumns}
        >
          {apiRef.current.getLocaleText('columnsManagementReset')}
        </BaseButton>
      </div>
    </>
  );
};

interface DataGridProps {
  columns: GridColDef[];
  rows?: any[];
  legacyParams?: LegacyParams;
  title?: string;
  headerActions?: React.ReactNode;
  onSearchChange?: (value: string) => void;
  rowHeight?: number;
  headerHeight?: number;
  onRowClick?: (params: any, event?: any) => void;
  checkboxSelection?: boolean;
  rowSelectionModel?: string[];
  onRowSelectionModelChange?: (ids: string[]) => void;
  initialState?: GridInitialStatePro;
  pageSizeOptions?: (number | { value: number; label: string })[] | undefined;
  columnVisibilityModel?: Record<string, boolean>;
  pinnedColumns?: { left?: string[]; right?: string[] };
  additionalSearchParams?: Record<string, any>;
  /**
   * When set, persists column order and visibility per-user to the userpreferences endpoint.
   * Must be unique per grid instance (e.g. 'qaQueuePeerReview').
   */
  gridId?: string;
  /** When set, the download button POSTs to this endpoint with the current filters and saves the response as a CSV file. */
  exportEndpoint?: string;
  /** Base name for the exported file (e.g. "UnderReviewQueue"). Defaults to "export". */
  exportFileName?: string;
  /** When true, active filters and quick-search are reflected in the URL and restored on first load. */
  enableUrlSync?: boolean;
  /** Optional prefix for URL params — use when multiple grids share the same page. Format: prefix[fieldName]=value */
  urlPrefix?: string;
  /**
   * Controls visibility of toolbar icons based on permissions.
   * Any key set to false hides that control entirely (not just disabled).
   */
  toolbarPermissions?: ToolbarPermissions;
  /**
   * Fields that "gate" the filter performance rule.
   * When provided, all columns whose field is NOT in this list are disabled
   * in the filter row until at least one gate-field filter is active.
   */
  filterGateFields?: string[];
  /**
   * When true, the quick search sends the currently visible column fields as `searchFields`
   * so the server only matches against columns the user can see.
   */
  searchVisibleColumnsOnly?: boolean;
  /** When true, the header filter row is shown by default on first load. */
  defaultShowFilters?: boolean;
  /** Initial page size for pagination. Defaults to 10. */
  initialPageSize?: number;
  /** Override the default no-rows overlay. Must be a stable component reference (defined outside render). */
  noRowsOverlay?: React.FC;
  /**
   * Called whenever the debounced column-filter values change. Use this to apply client-side
   * filtering externally when the grid is in client mode (no legacyParams).
   */
  onFilterValuesChange?: (values: Record<string, any>) => void;
  /** Show the skeleton loading overlay in client mode (no legacyParams). */
  loading?: boolean;
  /** When true, the grid re-fetches server data on a fixed interval. Only applies in server mode. Defaults to false. */
  enableAutoRefresh?: boolean;
  /** Interval in seconds between automatic re-fetches. Defaults to 180. Only used when enableAutoRefresh is true. */
  autoRefreshIntervalSeconds?: number;
}

// DateRangePickerInput emits "start|end" strings (see Form/DateTimePickers/DateRangePicker.tsx).
const DATE_RANGE_PIPE_RE = /^[^|]*\|[^|]*$/;

const urlKey = (field: string, prefix?: string) =>
  prefix ? `${prefix}[${field}]` : field;

// Merge this grid's new params into the current URL, preserving params this grid doesn't own.
// With a prefix: preserves any key that doesn't start with `prefix[`.
// Without a prefix: preserves any key whose base field isn't in knownFields.
const mergeGridParams = (
  newParams: URLSearchParams,
  prefix?: string,
  knownFields?: Set<string>
): string => {
  const current = new URLSearchParams(window.location.search);
  const result = new URLSearchParams();
  current.forEach((value, key) => {
    if (prefix) {
      if (!key.startsWith(`${prefix}[`)) result.append(key, value);
    } else if (knownFields) {
      const base = key
        .replace(/\[\]$/, '')
        .replace(/From$/, '')
        .replace(/To$/, '');
      if (!knownFields.has(base)) result.append(key, value);
    }
  });
  newParams.forEach((value, key) => result.append(key, value));
  return result.toString();
};

// Serialize filter values to URLSearchParams.
// Arrays use [] suffix; date ranges split to fieldFrom/fieldTo; searchKey maps directly.
const buildUrlParamsFromValues = (
  values: Record<string, any>,
  prefix?: string
): URLSearchParams => {
  const params = new URLSearchParams();
  for (const [field, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (!value.length) continue;
      params.set(urlKey(`${field}[]`, prefix), value.join('|'));
      continue;
    }
    if (typeof value === 'string' && DATE_RANGE_PIPE_RE.test(value)) {
      const [from, to] = value.split('|');
      if (from) params.set(urlKey(`${field}From`, prefix), from);
      if (to) params.set(urlKey(`${field}To`, prefix), to);
      continue;
    }
    params.set(urlKey(field, prefix), String(value));
  }
  return params;
};

// Deserialize URLSearchParams to a flat filter values record.
const parseUrlToFilterValues = (
  search: string,
  prefix?: string
): Record<string, any> => {
  const params = new URLSearchParams(search);
  const values: Record<string, any> = {};
  const seen = new Set<string>();

  const extractField = (rawKey: string): string | null => {
    if (!prefix) return rawKey;
    const pre = `${prefix}[`;
    return rawKey.startsWith(pre) && rawKey.endsWith(']')
      ? rawKey.slice(pre.length, -1)
      : null;
  };

  const fromFields = new Set<string>();
  params.forEach((_, rawKey) => {
    const field = extractField(rawKey);
    if (field?.endsWith('From')) fromFields.add(field.slice(0, -4));
  });

  params.forEach((_, rawKey) => {
    if (seen.has(rawKey)) return;
    seen.add(rawKey);
    const field = extractField(rawKey);
    if (!field) return;

    if (field === 'searchKey') {
      const val = params.get(rawKey);
      if (val) values.searchKey = val;
      return;
    }

    if (field.endsWith('[]')) {
      const baseField = field.slice(0, -2);
      const vals = (params.get(rawKey) ?? '').split('|').filter(Boolean);
      if (vals.length) values[baseField] = vals;
      return;
    }

    if (field.endsWith('To') && fromFields.has(field.slice(0, -2))) return;

    if (field.endsWith('From')) {
      const base = field.slice(0, -4);
      const from = params.get(rawKey) ?? '';
      const toRaw = urlKey(`${base}To`, prefix);
      const to = params.get(toRaw) ?? '';
      seen.add(toRaw);
      if (from || to) values[base] = `${from}|${to}`;
      return;
    }

    const val = params.get(rawKey);
    if (val) values[field] = val;
  });

  return values;
};

// Convert filter values to API request body.
// Splits date ranges (pipe format) into fieldFrom/fieldTo; searchKey handled separately.
const filterValuesToBody = (
  values: Record<string, any>
): Record<string, any> => {
  const body: Record<string, any> = {};
  for (const [field, value] of Object.entries(values)) {
    if (field === 'searchKey') continue;
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && !value.length) continue;
    if (typeof value === 'string' && DATE_RANGE_PIPE_RE.test(value)) {
      const [from, to] = value.split('|');
      if (from) body[`${field}From`] = from;
      if (to) body[`${field}To`] = to;
      continue;
    }
    body[field] = value;
  }
  return body;
};

export const DataGrid: React.FC<DataGridProps> = (props) => {
  const API = useApiWorker();
  const versionedAPI = useApiWorker(props.legacyParams?.apiVersion);
  const apiRef = useGridApiRef();
  const history = useHistory();
  const toast = useToasts();

  // Live refs so stable callbacks always see current values.
  const historyRef = React.useRef(history);
  const enableUrlSyncRef = React.useRef(props.enableUrlSync);
  const urlPrefixRef = React.useRef(props.urlPrefix);
  const onSearchChangeRef = React.useRef(props.onSearchChange);
  const columnsRef = React.useRef(props.columns);
  // Used to suppress the onFilterModelChange fired by the QuickFilter UI restoration call.
  const restoringQuickSearchRef = React.useRef(false);
  // Monotonic id for server fetches; only the latest response may update state.
  const requestSeqRef = React.useRef(0);
  historyRef.current = history;
  enableUrlSyncRef.current = props.enableUrlSync;
  urlPrefixRef.current = props.urlPrefix;
  onSearchChangeRef.current = props.onSearchChange;
  columnsRef.current = props.columns;

  // Parse URL once synchronously — seeds filterValues and showFilters on first render.
  // Only keep keys that match a known column field or searchKey; this prevents external
  // URL params (e.g. clientId/clientName written by ClientSelector) from leaking into
  // filterValues and re-appearing in the URL whenever a grid filter changes.
  const [initialUrlFilters] = React.useState<Record<string, any>>(() => {
    if (!props.enableUrlSync) return {};
    const parsed = parseUrlToFilterValues(
      window.location.search,
      props.urlPrefix
    );
    // A URL can't apply a search or filter the user has no permission for (STRAD2-2672 / TC-017);
    // otherwise the grid ends up filtered with no visible control to clear it.
    const allowSearch = props.toolbarPermissions?.showSearch !== false;
    const allowFilter = props.toolbarPermissions?.showFilter !== false;
    const knownFields = new Set([
      ...(allowFilter ? props.columns.map((c) => c.field) : []),
      ...(allowSearch ? ['searchKey'] : []),
    ]);
    return Object.keys(parsed).reduce<Record<string, any>>((acc, k) => {
      if (knownFields.has(k)) acc[k] = parsed[k];
      return acc;
    }, {});
  });

  const [preferencesReady, setPreferencesReady] = React.useState(!props.gridId);
  const [orderedFields, setOrderedFields] = React.useState<string[]>(() =>
    props.columns.map((c) => c.field)
  );

  React.useEffect(() => {
    if (!props.gridId) return;

    const getEndpoint = buildEndpoint(
      ENDPOINTS.userpreferences.endpointString('GetGridPreference')
    );
    const currentFields = props.columns.map((c) => c.field);

    API.post(getEndpoint, {
      preferenceType: 'GridColumnOrder',
      gridId: props.gridId,
    })
      .then((res: any) => {
        let savedOrder: string[] = [];
        let savedVisibility: Record<string, boolean> | null = null;

        try {
          const parsed = res?.data?.layoutJson
            ? JSON.parse(res.data.layoutJson)
            : null;
          if (
            Array.isArray(parsed) &&
            parsed.every((f: any) => typeof f === 'string')
          ) {
            savedOrder = parsed;
          } else if (
            parsed &&
            typeof parsed === 'object' &&
            Array.isArray(parsed.order)
          ) {
            savedOrder = (parsed.order as any[]).filter(
              (f) => typeof f === 'string'
            );
            if (parsed.visibility && typeof parsed.visibility === 'object') {
              savedVisibility = parsed.visibility;
            }
          }
        } catch {
          /* keep defaults */
        }

        if (savedOrder.length) {
          const valid = savedOrder.filter((f) => currentFields.includes(f));
          const missing = currentFields.filter((f) => !valid.includes(f));
          setOrderedFields([...valid, ...missing]);
        }

        if (savedVisibility) {
          setColumnVisibilityModel({
            ...(props.columnVisibilityModel ?? {}),
            ...savedVisibility,
          });
        }
      })
      .finally(() => {
        setPreferencesReady(true);
      });
  }, [props.gridId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveLayoutPreference = (
    order: string[],
    visibility: Record<string, boolean>
  ) => {
    // Without the columns permission the layout isn't the user's to change (the API refuses it too).
    if (!props.gridId || props.toolbarPermissions?.showColumns === false) return;
    const saveEndpoint = buildEndpoint(
      ENDPOINTS.userpreferences.endpointString('GridPreference')
    );
    API.post(saveEndpoint, {
      preferenceType: 'GridColumnOrder',
      gridId: props.gridId,
      layoutJson: JSON.stringify({ order, visibility }),
    }).catch(() => {
      /* silent fail */
    });
  };

  const handleColumnOrderChange = () => {
    if (!props.gridId || !apiRef.current) return;
    const fields = apiRef.current.getAllColumns().map((c: any) => c.field);
    setOrderedFields(fields);
    saveLayoutPreference(fields, columnVisibilityModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: props.initialPageSize ?? 10,
    page: 0,
  });
  // Show filter row if any column filter (not just searchKey) came from URL, or if defaultShowFilters is set.
  const [showFilters, setShowFilters] = React.useState(
    () =>
      props.defaultShowFilters ||
      Object.keys(initialUrlFilters).some((k) => k !== 'searchKey')
  );
  const defaultColumnVisibilityRef = React.useRef<Record<string, boolean>>(
    props.columnVisibilityModel ?? {}
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<
    Record<string, boolean>
  >(defaultColumnVisibilityRef.current);
  const isResettingColumnsRef = React.useRef(false);

  const handleColumnVisibilityChange = (model: Record<string, boolean>) => {
    if (isResettingColumnsRef.current) return;
    const visibleCount = props.columns.filter(
      (col) => model[col.field] !== false
    ).length;
    const next =
      visibleCount === 0 ? { ...defaultColumnVisibilityRef.current } : model;
    setColumnVisibilityModel(next);
    const toSave = { ...next };
    for (const [field, defaultVisible] of Object.entries(
      defaultColumnVisibilityRef.current
    )) {
      if (defaultVisible === false && next[field] !== false) {
        toSave[field] = true;
      }
    }
    saveLayoutPreference(orderedFields, toSave);

    const hiddenFields = Object.entries(next)
      .filter(([, visible]) => visible === false)
      .map(([field]) => field);
    const fieldsToRemove = hiddenFields.filter(
      (field) => field in filterValuesRef.current
    );
    if (fieldsToRemove.length) {
      const nextFilters = { ...filterValuesRef.current };
      fieldsToRemove.forEach((field) => delete nextFilters[field]);
      filterValuesRef.current = nextFilters;
      setFilterValues(nextFilters);
      clearTimeout(filterDebounceRef.current);
      setDebouncedFilterValues(nextFilters);
      setPaginationModel((prev) =>
        prev.page === 0 ? prev : { ...prev, page: 0 }
      );
      if (enableUrlSyncRef.current) {
        const newParams = buildUrlParamsFromValues(
          nextFilters,
          urlPrefixRef.current
        );
        const knownFields = new Set([
          ...columnsRef.current.map((c) => c.field),
          'searchKey',
        ]);
        historyRef.current.replace({
          search: mergeGridParams(newParams, urlPrefixRef.current, knownFields),
        });
      }
    }
  };

  const handleResetColumns = React.useCallback(() => {
    const defaultFields = props.columns.map((c) => c.field);
    const defaultVis = defaultColumnVisibilityRef.current;
    isResettingColumnsRef.current = true;
    apiRef.current?.setColumnVisibilityModel(defaultVis);
    // setColumnIndex fires `columnIndexChange` (not `columnOrderChange`), so
    // handleColumnOrderChange won't trigger and cause a duplicate save.
    defaultFields.forEach((field, index) => {
      try {
        apiRef.current?.setColumnIndex(field, index);
      } catch {
        /* ignore if column not mounted */
      }
    });
    isResettingColumnsRef.current = false;
    setOrderedFields(defaultFields);
    setColumnVisibilityModel(defaultVis);
    saveLayoutPreference(defaultFields, defaultVis);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [serverRows, setServerRows] = React.useState<any[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(!!props.legacyParams || !!props.loading);

  React.useEffect(() => {
    if (!props.legacyParams) setLoading(!!props.loading);
  }, [props.loading, props.legacyParams]);
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    () => (props.initialState as any)?.sorting?.sortModel ?? []
  );
  const [refreshTick, setRefreshTick] = React.useState(0);

  // filterValues: the single source of truth for all column header filter state.
  // Updated immediately by handleFilterValueChange — controlled components (MultiSelectFilter,
  // SingleSelectFilter, etc.) read from this via FilterContext so they stay in sync.
  const [filterValues, setFilterValues] =
    React.useState<Record<string, any>>(initialUrlFilters);
  const filterValuesRef = React.useRef<Record<string, any>>(initialUrlFilters);
  // debouncedFilterValues drives API calls — only updated after 300 ms of inactivity.
  const [debouncedFilterValues, setDebouncedFilterValues] =
    React.useState<Record<string, any>>(initialUrlFilters);
  const filterDebounceRef = React.useRef<ReturnType<typeof setTimeout>>();

  // Auto-refresh: increment refreshTick on each interval to re-trigger the fetch effect.
  // refreshTick is intentionally excluded — an auto-refresh must not reset its own timer.
  React.useEffect(() => {
    if (!props.enableAutoRefresh || !props.legacyParams) return;
    const intervalMs = (props.autoRefreshIntervalSeconds ?? 180) * 1000;
    const id = setInterval(() => setRefreshTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    props.enableAutoRefresh,
    props.autoRefreshIntervalSeconds,
    props.legacyParams,
    debouncedFilterValues,
    sortModel,
    paginationModel,
  ]);

  const isServerMode = !!props.legacyParams;

  // Joined into a string so the fetch effect only re-runs when the visible set actually changes
  // (props.columns is usually a new array on every parent render).
  const visibleSearchFieldsKey = props.searchVisibleColumnsOnly
    ? props.columns
        .filter((c) => columnVisibilityModel[c.field] !== false)
        .map((c) => c.field)
        .join(',')
    : '';
  const searchFieldsParam = React.useMemo<{ searchFields?: string[] }>(
    () =>
      visibleSearchFieldsKey
        ? { searchFields: visibleSearchFieldsKey.split(',') }
        : {},
    [visibleSearchFieldsKey]
  );

  const handleSearchCollapse = React.useCallback(() => {
    if (!filterValuesRef.current.searchKey) return;
    apiRef.current?.setQuickFilterValues([]);
  }, [apiRef]);

  // Restore the QuickFilter UI text from URL after the grid is ready.
  // Column header filters don't need restoration here — they read directly from filterValues.
  React.useEffect(() => {
    if (!preferencesReady || !props.enableUrlSync || !apiRef.current) return;
    const searchKey = filterValuesRef.current.searchKey;
    if (searchKey) {
      restoringQuickSearchRef.current = true;
      apiRef.current.setFilterModel({
        items: [],
        quickFilterValues: searchKey.split(' '),
      });
      restoringQuickSearchRef.current = false;
    }
  }, [preferencesReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent when debounced filter values change (used for client-side filtering).
  const onFilterValuesChangeRef = React.useRef(props.onFilterValuesChange);
  onFilterValuesChangeRef.current = props.onFilterValuesChange;
  React.useEffect(() => {
    onFilterValuesChangeRef.current?.(debouncedFilterValues);
  }, [debouncedFilterValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Called by every filter component when the user changes a value.
  // TextFieldFilter debounces internally (300 ms); all other filters call this immediately.
  // filterValues updates immediately (visual); debouncedFilterValues updates after 300 ms (API + URL).
  const handleFilterValueChange = React.useCallback(
    (field: string, value: any) => {
      const next = { ...filterValuesRef.current };
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        delete next[field];
      } else {
        next[field] = value;
      }
      filterValuesRef.current = next;
      setFilterValues(next);

      clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => {
        const flushed = { ...filterValuesRef.current };
        setDebouncedFilterValues(flushed);
        setPaginationModel((prev) =>
          prev.page === 0 ? prev : { ...prev, page: 0 }
        );
        if (enableUrlSyncRef.current) {
          const newParams = buildUrlParamsFromValues(
            flushed,
            urlPrefixRef.current
          );
          const knownFields = new Set([
            ...columnsRef.current.map((c) => c.field),
            'searchKey',
          ]);
          historyRef.current.replace({
            search: mergeGridParams(
              newParams,
              urlPrefixRef.current,
              knownFields
            ),
          });
        }
      }, 300);
    },
    []
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Only intercepts QuickFilter changes — column header filters bypass MUI's filterModel entirely.
  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      if (restoringQuickSearchRef.current) return;
      if (model.quickFilterValues === undefined) return;
      const searchKey = model.quickFilterValues.join(' ');
      if (onSearchChangeRef.current) onSearchChangeRef.current(searchKey);
      handleFilterValueChange('searchKey', searchKey || undefined);
    },
    [handleFilterValueChange]
  );

  const computedInitialState = React.useMemo(
    () => ({
      ...props.initialState,
      filter: {
        ...props.initialState?.filter,
        filterModel: props.initialState?.filter?.filterModel ?? { items: [] },
      },
      columns: {
        ...props.initialState?.columns,
        orderedFields,
        // Tells MUI what the "initial" visibility is so the Reset button restores our actual defaults.
        columnVisibilityModel: defaultColumnVisibilityRef.current,
      },
    }),
    [orderedFields]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isServerMode) return;

    const page = paginationModel.page + 1;
    const pageSize =
      paginationModel.pageSize === -1 ? 100000 : paginationModel.pageSize;
    const sortQuery = props.legacyParams?.apiVersion
      ? sortModel.length > 0
        ? `sortBy=${encodeURIComponent(sortModel[0].field)}&sortDir=${sortModel[0].sort ?? 'asc'}`
        : ''
      : sortModel
          .map(
            (s, i) =>
              `sort[${i}][field]=${encodeURIComponent(s.field)}&sort[${i}][dir]=${s.sort ?? 'asc'}`
          )
          .join('&');
    const url = `${props.legacyParams!.readEndpoint}?page=${page}&pageSize=${pageSize}${sortQuery ? '&' + sortQuery : ''}`;
    const searchKey = debouncedFilterValues.searchKey ?? '';
    const body = {
      ...props.legacyParams!.searchParams,
      ...filterValuesToBody(debouncedFilterValues),
      ...(searchKey ? { searchKey, ...searchFieldsParam } : {}),
      ...(props.additionalSearchParams ?? {}),
    };

    // Responses are not guaranteed to come back in request order, so a stale
    // in-flight response could otherwise overwrite newer rows (STRAD2-2628).
    const seq = ++requestSeqRef.current;

    setLoading(true);
    versionedAPI
      .post(url, body)
      .then((response: any) => {
        if (seq !== requestSeqRef.current) return;
        if (response?.data) {
          setServerRows(response.data.data ?? []);
          setRowCount(response.data.total ?? 0);
        }
      })
      .finally(() => {
        if (seq !== requestSeqRef.current) return;
        setLoading(false);
      });
  }, [
    paginationModel,
    debouncedFilterValues,
    sortModel,
    props.legacyParams,
    props.additionalSearchParams,
    refreshTick,
    // Only re-fetch on column visibility changes while a search is active.
    debouncedFilterValues.searchKey ? visibleSearchFieldsKey : undefined,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveColumns = React.useMemo(() => {
    const colMap = new Map<string, any>(props.columns.map((c) => [c.field, c]));
    const base = orderedFields.length
      ? [
          ...orderedFields
            .filter((f) => colMap.has(f))
            .map((f) => colMap.get(f)),
          ...props.columns.filter((c) => !orderedFields.includes(c.field)),
        ]
      : props.columns;
    return base.map((col: any) =>
      showFilters && col.widthOnFilter !== undefined
        ? { ...col, width: col.widthOnFilter }
        : col
    );
  }, [props.columns, showFilters, orderedFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = React.useCallback(async () => {
    if (!props.exportEndpoint) return;
    const searchKey = debouncedFilterValues.searchKey ?? '';
    const columns = effectiveColumns
      .filter((col) => columnVisibilityModel[col.field] !== false)
      .map((col) => ({
        field: col.field,
        headerName: col.headerName ?? col.field,
      }));
    const body = {
      ...props.legacyParams?.searchParams,
      ...filterValuesToBody(debouncedFilterValues),
      ...(searchKey ? { searchKey, ...searchFieldsParam } : {}),
      ...(props.additionalSearchParams ?? {}),
      columns,
      timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    try {
      const response = await versionedAPI.post(props.exportEndpoint, body, {
        responseType: 'blob',
      });
      const ts = new Date()
        .toISOString()
        .replace(/[-T:.Z]/g, '')
        .slice(0, 14);
      const fileName = `${props.exportFileName ?? 'export'}_${ts}.csv`;
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Without this the request fails silently and the user is left waiting
      // for a file that never downloads (STRAD2-2637).
      toast.error(
        (err as any)?.ErrorMessage ??
          'Error: the export could not be generated. No file was downloaded.'
      );
    }
  }, [
    props.exportEndpoint,
    props.legacyParams?.searchParams,
    props.additionalSearchParams,
    debouncedFilterValues,
    versionedAPI,
    effectiveColumns,
    columnVisibilityModel,
    searchFieldsParam,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute disabled fields for the filter performance rule.
  // A field is disabled when filterGateFields is set AND none of the gate fields have an active value.
  const disabledFields = React.useMemo<Set<string> | undefined>(() => {
    if (!props.filterGateFields?.length) return undefined;
    const gateActive = props.filterGateFields.some((f) => {
      const v = filterValues[f];
      return (
        v !== undefined &&
        v !== null &&
        v !== '' &&
        !(Array.isArray(v) && v.length === 0)
      );
    });
    if (gateActive) return undefined;
    const allFields = new Set(props.columns.map((c) => c.field));
    props.filterGateFields.forEach((f) => allFields.delete(f));
    return allFields;
  }, [filterValues, props.filterGateFields, props.columns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable context value — onChange is stable, values changes when a filter changes.
  const filterContextValue = React.useMemo<FilterContextValue>(
    () => ({
      values: filterValues,
      onChange: handleFilterValueChange,
      disabledFields,
    }),
    [filterValues, handleFilterValueChange, disabledFields]
  );

  // Hiding the toolbar icons isn't enough on its own: a filter in the URL would still open the
  // filter row, and the column header menu offers Hide column / Manage columns (STRAD2-2672 / TC-017).
  const canFilter = props.toolbarPermissions?.showFilter !== false;
  const canManageColumns = props.toolbarPermissions?.showColumns !== false;

  if (!preferencesReady) {
    return (
      <Box className="datagrid-card">
        <GridLoadingSkeleton title={props.title} />
      </Box>
    );
  }

  return (
    <FilterContext.Provider value={filterContextValue}>
      <Box className="datagrid-card">
        <DataGridPro
          apiRef={apiRef}
          columns={effectiveColumns}
          rows={isServerMode ? serverRows : (props.rows ?? [])}
          rowHeight={props.rowHeight || 48}
          initialState={computedInitialState}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          onColumnOrderChange={handleColumnOrderChange}
          pinnedColumns={props.pinnedColumns}
          onRowClick={props.onRowClick}
          checkboxSelection={props.checkboxSelection}
          disableRowSelectionOnClick={props.checkboxSelection}
          rowSelectionModel={
            props.rowSelectionModel !== undefined
              ? { type: 'include' as const, ids: new Set(props.rowSelectionModel) }
              : undefined
          }
          onRowSelectionModelChange={
            props.onRowSelectionModelChange
              ? (model) => {
                  if (model.type === 'include') {
                    props.onRowSelectionModelChange!(
                      Array.from(model.ids).map(String)
                    );
                  } else {
                    // 'exclude': MUI uses this for "select all" — expand to all
                    // visible row IDs minus the explicitly excluded ones
                    const excluded = new Set(Array.from(model.ids).map(String));
                    const all = (apiRef.current?.getAllRowIds() ?? [])
                      .map(String)
                      .filter((id) => !excluded.has(id));
                    props.onRowSelectionModelChange!(all);
                  }
                }
              : undefined
          }
          columnHeaderHeight={48}
          headerFilterHeight={56}
          disableColumnFilter
          headerFilters={showFilters && canFilter}
          disableColumnSelector={!canManageColumns}
          disableColumnReorder={!canManageColumns}
          showToolbar
          pagination
          paginationMode={isServerMode ? 'server' : 'client'}
          filterMode={isServerMode ? 'server' : 'client'}
          sortingMode={isServerMode ? 'server' : 'client'}
          rowCount={isServerMode ? rowCount : undefined}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100, { value: -1, label: 'All' }]}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onFilterModelChange={handleFilterModelChange}
          loading={loading}
          slots={{
            headerFilterMenu: null,
            toolbar: CustomToolbar,
            loadingOverlay: SkeletonLoadingOverlay,
            noRowsOverlay: props.noRowsOverlay ?? NoRowsOverlay,
            columnsManagement: CustomColumnsManagement,
          }}
          slotProps={{
            toolbar: {
              title: props.title,
              headerActions: props.headerActions,
              showFilters,
              onToggleFilters: () => setShowFilters((v) => !v),
              onExport: props.exportEndpoint ? handleExport : undefined,
              onSearchCollapse: handleSearchCollapse,
              toolbarPermissions: props.toolbarPermissions,
            } as any,
            ...(canManageColumns
              ? {}
              : {
                  columnMenu: {
                    slots: { columnMenuColumnsItem: null, columnMenuPinningItem: null },
                  } as any,
                }),
            columnsManagement: {
              onResetColumns: handleResetColumns,
              defaultVisibility: defaultColumnVisibilityRef.current,
              defaultFields: props.columns.map((c) => c.field),
            } as any,
            basePopper: {
              popperOptions: {
                strategy: 'fixed',
                modifiers: [
                  {
                    name: 'flip',
                    enabled: true,
                    options: { fallbackPlacements: ['top-start', 'top-end'] },
                  },
                  {
                    name: 'preventOverflow',
                    enabled: true,
                    options: { altAxis: true },
                  },
                ],
              },
            } as any,
            basePagination: {
              SelectProps: { IconComponent: ArrowDropDownRoundedIcon },
            } as any,
          }}
        />
      </Box>
    </FilterContext.Provider>
  );
};
export default DataGrid;
